import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSub, setActiveSub] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Only lock scroll on open. Unlocking happens in AnimatePresence's
  // onExitComplete below, so it doesn't race the close animation.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    }
  }, [mobileOpen]);

  const menuData = [
    {
      category: "General Dentistry",
      icon: "🦷",
      services: [
        { label: "Pain Free Root Canal", url: "/treatments/pain-free-root-canal" },
        { label: "Teeth Cleaning", url: "/treatments/teeth-cleaning" },
        { label: "Crowns & Bridges", url: "/treatments/crowns-bridges" },
        { label: "Real-Feel Dentures", url: "/treatments/dentures" },
        { label: "Natural Restorations", url: "/treatments/dental-restorations" },
        { label: "Mouthguards", url: "/treatments/mouthguards" },
      ]
    },
    {
      category: "Cosmetic & Aesthetic",
      icon: "✨",
      services: [
        { label: "Smile Makeovers", url: "/treatments/smile-makeover" },
        { label: "Zoom Whitening", url: "/treatments/teeth-whitening" },
        { label: "Gum Depigmentation", url: "/treatments/gum-depigmentation" },
        { label: "Full Mouth Rehab", url: "/treatments/full-mouth-rehabilitation" },
        { label: "Composite Veneers", url: "/treatments/composite-veneers" },
      ]
    },
    {
      category: "Surgical & Implants",
      icon: "🏥",
      services: [
        { label: "Wisdom Tooth Removal", url: "/treatments/wisdom-tooth-removal" },
        { label: "Full Mouth Implants", url: "/treatments/full-mouth-implants" },
        { label: "Bone Grafting", url: "/treatments/bone-grafting" },
        { label: "Non-Graft Implants", url: "/treatments/implants-without-bone-grafting" },
      ]
    },
    {
      category: "Specialized Care",
      icon: "👶",
      services: [
        { label: "Dental Sealants", url: "/treatments/dental-sealants" },
        { label: "Fluoride Treatment", url: "/treatments/fluoride-treatment" },
        { label: "Invisible Braces", url: "/treatments/invisible-braces" },
        { label: "Metal Braces", url: "/treatments/metal-braces" },
      ]
    }
  ];

  return (
    <>
      <header className={`universal-header ${isScrolled ? "is-sticky" : ""}`}>
        <div className="container-fluid px-lg-5">
          <div className="header-inner-flex">

            {/* LOGO - ALWAYS VISIBLE LOGIC */}
            <div className="site-logo">
              <Link to="/">
                {/* Logic: On desktop (>991px), use Light logo if not scrolled.
                   On Mobile, ALWAYS use the dark logo (SMILAX.png) for visibility.
                */}
                <picture>
                  <source
                    media="(max-width: 991px)"
                    srcSet="/assets/img/SMILAX.png"
                  />
                  <img
                    src={isScrolled ? "/assets/img/SMILAX.png" : "/assets/img/SMILAX_LIGHT.png"}
                    alt="Smilax"
                  />
                </picture>
              </Link>
            </div>

            <nav className="d-none d-xl-block">
              <ul className="desktop-main-nav">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about-us">About</Link></li>
                <li className="mega-hover-item">
                  <a href="#">Treatments <i className="fa fa-chevron-down ms-1"></i></a>
                  <div className="mega-panel-full">
                    <div className="container-fluid px-lg-5">
                      <div className="mega-panel-grid">
                        {menuData.map((section, idx) => (
                          <div className="mega-section-col" key={idx}>
                            <div className="mega-cat-label">
                              <span className="icon-wrap">{section.icon}</span>
                              <h6>{section.category}</h6>
                            </div>
                            <ul className="mega-sub-list">
                              {section.services.map((s, si) => (
                                <li key={si}><Link to={s.url}>{s.label}</Link></li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>
                <li><Link to="/gallery">Gallery</Link></li>
                <li><Link to="/reviews">Reviews</Link></li>
                <li><Link to="/contact-us">Contact</Link></li>
              </ul>
            </nav>

            <div className="header-action-group">
              <a href="tel:9321133580" className="nav-phone-pill d-none d-md-flex">
                <span className="live-pulse-dot"></span> +91 9321133580
              </a>
              <Link to="/appointment" className="nav-cta-premium d-none d-lg-block">Book Appointment</Link>

              <button className="mobile-menu-trigger d-xl-none" onClick={() => setMobileOpen(true)}>
                <span></span>
                <span></span>
              </button>
            </div>

          </div>
        </div>
      </header>

      <AnimatePresence onExitComplete={() => { document.body.style.overflow = "auto"; }}>
        {mobileOpen && (
          <motion.div
            className="mobile-navigation-overlay"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
          >
            <div className="mob-menu-header">
              <img src="/assets/img/SMILAX.png" className="mob-logo-small" alt="Logo" />
              <button className="mob-close-btn" onClick={() => setMobileOpen(false)}>
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="mob-menu-scrollable">
              <div className="mob-top-links">
                <Link to="/" onClick={() => setMobileOpen(false)}>Home</Link>
                <Link to="/about-us" onClick={() => setMobileOpen(false)}>Our Story</Link>
              </div>

              <div className="mob-treatments-section">
                <p className="mob-section-tag">Clinical Services</p>
                {menuData.map((section, idx) => (
                  <div key={idx} className={`mob-cat-card ${activeSub === idx ? "is-active" : ""}`}>
                    <div className="mob-cat-trigger" onClick={() => setActiveSub(activeSub === idx ? null : idx)}>
                      <span className="mob-cat-text">{section.icon} {section.category}</span>
                      <span className="toggle-icon">{activeSub === idx ? "−" : "+"}</span>
                    </div>
                    <AnimatePresence>
                      {activeSub === idx && (
                        <motion.div
                          className="mob-sub-drawer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          {section.services.map((s, si) => (
                            <Link to={s.url} key={si} onClick={() => setMobileOpen(false)} className="mob-service-link">
                              {s.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>

              <div className="mob-bottom-links mt-4">
                <Link to="/reviews" onClick={() => setMobileOpen(false)}>Patient Reviews</Link>
                <Link to="/gallery" onClick={() => setMobileOpen(false)}>Gallery</Link>
                <Link to="/contact-us" onClick={() => setMobileOpen(false)}>Contact Us</Link>
              </div>
            </div>

            {/* <div className="mob-fixed-footer">
              <Link to="/appointment" className="mob-full-cta">Book Appointment</Link>
            </div> */}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .universal-header {
          position: fixed; top: 0; left: 0; width: 100%; z-index: 9999;
          padding: 24px 0; transition: 0.4s ease; background: transparent;
        }

        .is-sticky {
          padding: 12px 0; background: rgba(255, 255, 255, 0.98) !important;
          backdrop-filter: blur(20px); box-shadow: 0 4px 30px rgba(0,0,0,0.06);
        }

        .header-inner-flex { display: flex; align-items: center; justify-content: space-between; }

        .site-logo img { height: 78px; transition: 0.3s; width: auto; display: block; }

        /* DESKTOP NAV */
        .desktop-main-nav { display: flex; list-style: none; margin: 0; gap: 32px; padding: 0; }
        .desktop-main-nav li a {
          text-decoration: none; color: white; font-weight: 700; font-size: 14px;
          text-transform: uppercase; letter-spacing: 1.2px; transition: 0.3s;
        }
        .is-sticky .desktop-main-nav li a { color: #0f172a; }
        .desktop-main-nav li a:hover { color: #3b82f6 !important; }

        /* MEGA PANEL */
        .mega-hover-item { position: static !important; }
        .mega-panel-full {
          position: absolute; top: 100%; left: 0; width: 100vw;
          background: white; padding: 50px 0; opacity: 0; visibility: hidden;
          transform: translateY(15px); transition: 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 40px 100px rgba(0,0,0,0.1); border-top: 1px solid #f1f5f9;
        }
        .mega-hover-item:hover .mega-panel-full { opacity: 1; visibility: visible; transform: translateY(0); }
        .mega-panel-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 40px; }
        .mega-cat-label { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; }
        .icon-wrap { width: 34px; height: 34px; background: #f0f7ff; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .mega-cat-label h6 { font-weight: 800; color: #1e293b; margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 1px; }
        .mega-sub-list { list-style: none; padding: 0; }
        .mega-sub-list li a { display: block; color: #64748b !important; font-size: 14px !important; font-weight: 500 !important; padding: 8px 0; text-transform: none !important; letter-spacing: 0 !important; }
        .mega-sub-list li a:hover { color: #3b82f6 !important; transform: translateX(8px); }

        /* ACTIONS */
        .header-action-group { display: flex; align-items: center; gap: 20px; }
        .nav-phone-pill { background: rgba(59, 130, 246, 0.1); color: #3b82f6; padding: 10px 20px; border-radius: 50px; text-decoration: none; font-weight: 800; font-size: 13px; display: flex; align-items: center; gap: 10px; }
        .live-pulse-dot { width: 8px; height: 8px; background: #3b82f6; border-radius: 50%; box-shadow: 0 0 10px #3b82f6; }
        .nav-cta-premium { background: #233657; color: white !important; padding: 12px 30px; border-radius: 50px; font-weight: 800; font-size: 13px; text-decoration: none; box-shadow: 0 10px 20px rgba(35, 54, 87, 0.2); transition: 0.3s; }

        .mobile-menu-trigger { background: none; border: none; display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
        .mobile-menu-trigger span { width: 28px; height: 3px; background: white; border-radius: 10px; transition: 0.3s; }
        .is-sticky .mobile-menu-trigger span { background: #0f172a; }

        /* MOBILE MENU OVERLAY */
        .mobile-navigation-overlay {
          position: fixed; inset: 0; background: #ffffff; z-index: 10001;
          display: flex; flex-direction: column; padding: 20px;
          will-change: transform;
        }
        .mob-menu-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
        .mob-logo-small { height: 32px; width: auto; }
        .mob-close-btn { font-size: 24px; color: #1e293b; background: #f1f5f9; border: none; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
        .mob-menu-scrollable { flex: 1; overflow-y: auto; padding: 20px 0; margin-bottom: 80px; }
        .mob-top-links a, .mob-bottom-links a { display: block; font-size: 18px; font-weight: 700; color: #1e293b; text-decoration: none; margin-bottom: 20px; }
        .mob-section-tag { font-weight: 800; color: #3b82f6; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; margin: 20px 0 15px; }
        .mob-cat-card { background: #f8fafc; border-radius: 16px; margin-bottom: 12px; transition: 0.3s; border: 1px solid transparent; }
        .is-active { border-color: #3b82f6; background: #ffffff; box-shadow: 0 10px 30px rgba(59, 130, 246, 0.05); }
        .mob-cat-trigger { padding: 18px; display: flex; justify-content: space-between; align-items: center; }
        .mob-cat-text { font-weight: 700; color: #1e293b; font-size: 15px; }
        .toggle-icon { font-size: 20px; color: #3b82f6; font-weight: 400; }
        .mob-sub-drawer { display: flex; flex-direction: column; padding: 0 18px 18px; }
        .mob-service-link { padding: 14px 0; color: #64748b; text-decoration: none; font-size: 14px; font-weight: 500; border-bottom: 1px solid #f1f5f9; }
        .mob-fixed-footer { position: absolute; bottom: 0; left: 0; width: 100%; padding: 20px; background: white; border-top: 1px solid #f1f5f9; }
        .mob-full-cta { background: #233657; color: white; display: block; text-align: center; padding: 18px; border-radius: 16px; font-weight: 800; text-decoration: none; }

        @media (max-width: 991px) {
          /* FORCE VISIBILITY ON MOBILE BEFORE SCROLL:
             Make background white and hamburger dark immediately.
          */
          .universal-header {
            padding: 12px 0;
            background: white !important;
            box-shadow: 0 2px 15px rgba(0,0,0,0.05);
          }
          .site-logo img { height: 32px; }
          .mobile-menu-trigger span { background: #0f172a !important; }
        }
      `}</style>
    </>
  );
};

export default Header;